import { useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, UsersThree, EnvelopeSimple, Trash, Buildings } from "@phosphor-icons/react";

export default function OrgPage() {
  const [orgs, setOrgs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [members, setMembers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");

  const loadOrgs = async () => {
    try {
      const { data } = await api.get("/orgs");
      setOrgs(data);
      if (!selected && data.length > 0) setSelected(data[0]);
    } catch {
      toast.error("Failed to load organizations");
    }
  };
  useEffect(() => { loadOrgs(); /* eslint-disable-next-line */ }, []);

  const loadMembers = async (orgId) => {
    try {
      const { data } = await api.get(`/orgs/${orgId}/members`);
      setMembers(data);
    } catch {
      setMembers([]);
    }
  };
  useEffect(() => { if (selected) loadMembers(selected.id); }, [selected]);

  const createOrg = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/orgs", { name: newName });
      toast.success("Organization created");
      setShowNew(false);
      setNewName("");
      await loadOrgs();
      setSelected(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    if (!selected) return;
    try {
      await api.post(`/orgs/${selected.id}/invite`, { email: inviteEmail, role: inviteRole });
      toast.success(`Invite sent to ${inviteEmail}`);
      setInviteEmail("");
      loadMembers(selected.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeMember = async (userId) => {
    if (!confirm("Remove this member?")) return;
    try {
      await api.delete(`/orgs/${selected.id}/members/${userId}`);
      toast.success("Member removed");
      loadMembers(selected.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const isAdmin = selected && ["owner", "admin"].includes(selected.my_role);

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar active="orgs" />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-tag mb-2">// Workspaces</div>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Organizations</h1>
            <p className="text-gray-400 mt-2 max-w-xl">
              Group your projects, invite teammates, and collaborate as a firm.
            </p>
          </div>
          <button onClick={() => setShowNew(true)} className="btn-primary inline-flex items-center gap-2" data-testid="new-org-btn">
            <Plus weight="bold" size={18} /> New organization
          </button>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-px bg-white/10 border border-white/10">
          {/* Org list */}
          <aside className="bg-[#0d1015] p-4">
            <div className="label-tag mb-2">// Your orgs</div>
            {orgs.length === 0 && <div className="text-gray-500 text-sm font-mono">No orgs yet.</div>}
            {orgs.map((o) => (
              <button
                key={o.id}
                onClick={() => setSelected(o)}
                className={`w-full text-left px-3 py-3 mb-1 transition border-l-2 ${selected?.id === o.id ? "border-[#00E5FF] bg-white/5" : "border-transparent hover:bg-white/5"}`}
                data-testid={`org-item-${o.id}`}
              >
                <div className="flex items-center gap-2">
                  <Buildings size={16} className="text-[#00E5FF]" />
                  <div>
                    <div className="font-display font-bold text-sm">{o.name}</div>
                    <div className="label-tag text-[10px]">{o.my_role} · {o.member_count} members</div>
                  </div>
                </div>
              </button>
            ))}
          </aside>

          {/* Selected org detail */}
          <section className="bg-[#12151C] p-6 min-h-[400px]">
            {!selected ? (
              <div className="text-gray-500 font-mono text-sm">Create or select an organization.</div>
            ) : (
              <>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="label-tag">// Organization</div>
                    <h2 className="font-display text-2xl font-bold mt-1" data-testid="org-name">{selected.name}</h2>
                    <div className="font-mono text-xs text-gray-500 mt-2">
                      Created {new Date(selected.created_at).toLocaleDateString()} · Your role: <span className="text-[#00E5FF] uppercase">{selected.my_role}</span>
                    </div>
                  </div>
                </div>

                {/* Invite */}
                {isAdmin && (
                  <form onSubmit={invite} className="mt-6 border border-white/10 p-4" data-testid="invite-form">
                    <div className="label-tag mb-2">// Invite by email</div>
                    <div className="flex flex-wrap gap-2">
                      <input
                        type="email"
                        required
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="teammate@example.com"
                        className="flex-1 min-w-[200px] bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
                        data-testid="invite-email"
                      />
                      <select
                        value={inviteRole}
                        onChange={(e) => setInviteRole(e.target.value)}
                        className="bg-black/40 border border-white/10 px-2 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
                        data-testid="invite-role"
                      >
                        <option value="admin">admin</option>
                        <option value="editor">editor</option>
                        <option value="viewer">viewer</option>
                      </select>
                      <button type="submit" className="btn-primary text-sm py-2 inline-flex items-center gap-2" data-testid="invite-submit">
                        <EnvelopeSimple size={16} /> Send invite
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-gray-500 font-mono">
                      Existing users join immediately. New emails get an invite — they'll be activated automatically when they register or sign in.
                    </p>
                  </form>
                )}

                {/* Members */}
                <div className="mt-6">
                  <div className="label-tag mb-2 flex items-center gap-2">
                    <UsersThree size={14} /> Members ({members.length})
                  </div>
                  <div className="border border-white/10" data-testid="members-list">
                    {members.map((m) => (
                      <div key={m.email} className="border-b border-white/5 last:border-0 p-3 flex items-center justify-between font-mono text-sm">
                        <div>
                          <div className="text-white" data-testid={`member-${m.email}`}>{m.name || m.email}</div>
                          <div className="text-gray-500 text-xs">{m.email}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`label-tag ${m.status === "invited" ? "!text-[#FFCC00]" : "!text-[#00E5FF]"}`}>
                            {m.status} · {m.role}
                          </span>
                          {isAdmin && m.role !== "owner" && (
                            <button onClick={() => removeMember(m.user_id)} className="text-gray-500 hover:text-red-400" title="Remove" data-testid={`remove-${m.email}`}>
                              <Trash size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </div>

      {/* New org modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <form onSubmit={createOrg} className="bg-[#12151C] border border-white/10 w-full max-w-md p-6" data-testid="new-org-modal">
            <div className="label-tag">// Create organization</div>
            <h2 className="font-display text-2xl font-bold mt-2">New organization</h2>
            <label className="block mt-4">
              <span className="label-tag">Name</span>
              <input
                required
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Acme Engineering Ltd."
                className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
                data-testid="new-org-name"
              />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setShowNew(false)} className="btn-ghost" data-testid="new-org-cancel">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="new-org-create">Create</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
